/**
 * O card aberto: título, coluna, responsáveis, checklist e conversa.
 *
 * <p>Não é o {@code ModalFrente} com props a menos. Aquele tem bloco e janela de meses no
 * cabeçalho, que é o que uma frente do roadmap <i>é</i>; um card não tem nem um nem outro
 * — tem coluna. Fundir os dois deixaria metade do cabeçalho escondida atrás de um
 * {@code if} por linha.
 *
 * <p>O que os dois compartilham de verdade — checklist e conversa — é compartilhado de
 * verdade: os mesmos componentes, nas mesmas rotas. Era a promessa da V28, e é por isso
 * que comentar num card não precisou de uma linha de backend nova.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../api/cliente';
import type { Membro } from '../../api/tipos';
import { CorpoDaTarefa } from '../CorpoDaTarefa';
import type { Card, Quadro, Tag } from './tipos';
import { CORES } from './cores';
import '../modal.css';

/** O card, mais a coluna em que ele está — que é dela, e não dele. */
export type CardAberto = Card & { colunaId: number };

export function ModalCard({
  card,
  colunas,
  membros,
  tags,
  slug,
  aplicar,
  fechar,
}: {
  card: CardAberto;
  colunas: { id: number; nome: string }[];
  membros: Membro[];
  /** O catálogo de tags do quadro — não só as que este card já tem. */
  tags: Tag[];
  slug: string;
  /** Substitui o quadro no cache: toda escrita daqui devolve ele inteiro. */
  aplicar(quadro: Quadro): void;
  fechar(): void;
}) {
  const [nome, setNome] = useState(card.nome);
  const debounceNome = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [criandoTag, setCriandoTag] = useState(false);
  const [nomeTag, setNomeTag] = useState('');
  const [paletaDaTag, setPaletaDaTag] = useState<number | null>(null);

  const editar = useMutation({
    mutationFn: (corpo: {
      nome?: string;
      descricao?: string;
      concluida?: boolean;
      colunaId?: number;
      responsaveis?: number[];
      tags?: number[];
    }) =>
      api<Quadro>(`/api/projetos/${slug}/quadro/cards/${card.id}`, {
        method: 'PATCH',
        body: JSON.stringify(corpo),
      }),
    onSuccess: aplicar,
  });

  const criarTag = useMutation({
    mutationFn: (nomeNovo: string) =>
      api<Quadro>(`/api/projetos/${slug}/quadro/tags`, {
        method: 'POST',
        body: JSON.stringify({ nome: nomeNovo, cor: CORES[tags.length % CORES.length] }),
      }),
    onSuccess: (q) => {
      aplicar(q);
      setCriandoTag(false);
      const criada = q.tags.find((t) => t.nome === nomeTag.trim());
      setNomeTag('');
      // A tag nasce e já entra no card — foi o que a pessoa pediu ao digitar aqui,
      // não só "adicionar ao catálogo".
      if (criada) editar.mutate({ tags: [...card.tags.map((t) => t.id), criada.id] });
    },
  });

  const editarTag = useMutation({
    mutationFn: ({ id, cor }: { id: number; cor: string }) =>
      api<Quadro>(`/api/projetos/${slug}/quadro/tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ cor }),
      }),
    onSuccess: (q) => {
      aplicar(q);
      setPaletaDaTag(null);
    },
  });

  const excluirTag = useMutation({
    mutationFn: (id: number) =>
      api<Quadro>(`/api/projetos/${slug}/quadro/tags/${id}`, { method: 'DELETE' }),
    onSuccess: aplicar,
  });

  const excluir = useMutation({
    mutationFn: () =>
      api<Quadro>(`/api/projetos/${slug}/quadro/cards/${card.id}`, { method: 'DELETE' }),
    onSuccess: (q) => {
      aplicar(q);
      fechar();
    },
  });

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // A paleta fecha primeiro: apertar Esc para sair dela não pode levar o modal
      // inteiro junto.
      if (paletaDaTag != null) setPaletaDaTag(null);
      else fechar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [fechar, paletaDaTag]);

  useEffect(() => {
    if (paletaDaTag == null) return;
    function aoClicarFora(e: MouseEvent) {
      if (!(e.target as Element)?.closest('.db-etq-envolt')) setPaletaDaTag(null);
    }
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [paletaDaTag]);

  function renomear(valor: string) {
    setNome(valor);
    clearTimeout(debounceNome.current);
    debounceNome.current = setTimeout(() => {
      if (valor.trim() && valor.trim() !== card.nome) editar.mutate({ nome: valor.trim() });
    }, 500);
  }

  function alternarResponsavel(membroId: number) {
    const atuais = new Set(card.responsaveis);
    if (atuais.has(membroId)) atuais.delete(membroId);
    else atuais.add(membroId);
    editar.mutate({ responsaveis: [...atuais] });
  }

  function alternarTag(tagId: number) {
    const atuais = new Set(card.tags.map((t) => t.id));
    if (atuais.has(tagId)) atuais.delete(tagId);
    else atuais.add(tagId);
    editar.mutate({ tags: [...atuais] });
  }

  function apagarTag(tag: Tag) {
    if (!confirm(`Excluir a tag "${tag.nome}"?\nEla sai de todos os cards que a usam.`)) return;
    if (paletaDaTag === tag.id) setPaletaDaTag(null);
    excluirTag.mutate(tag.id);
  }

  function apagar() {
    if (!confirm(`Excluir o card "${card.nome}"?\nO checklist e a conversa vão junto.`)) return;
    excluir.mutate();
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && fechar()}>
      <div className="modal tarefa" role="dialog" aria-modal="true">
        <div className="mhead">
          <div className="mtop">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mchip">
                <span className="sw" style={{ background: 'var(--gold)' }} />
                Dev Board
              </div>
              <input
                className="mtitle-in"
                value={nome}
                onChange={(e) => renomear(e.target.value)}
                placeholder="O que precisa ser feito?"
              />
            </div>
            <button className="mclose" onClick={fechar} aria-label="Fechar">
              ×
            </button>
          </div>
        </div>

        <CorpoDaTarefa
          tarefaId={card.id}
          membros={membros}
          descricao={card.descricao}
          aoSalvarDescricao={(descricao) => editar.mutate({ descricao })}
          concluida={card.concluida}
          aoAlternarConcluida={() => editar.mutate({ concluida: !card.concluida })}
          rotuloConcluida="Card"
          responsaveis={card.responsaveis}
          aoAlternarResponsavel={alternarResponsavel}
          comentarios={card.comentarios}
          aoExcluir={apagar}
          contexto={
            <>
              <h4>Coluna</h4>
              <select
                className="tf-select"
                value={card.colunaId}
                onChange={(e) => editar.mutate({ colunaId: Number(e.target.value) })}
              >
                {colunas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>

              <h4>Tags</h4>
              <div className="db-tags-picker">
                {tags.map((tag) => {
                  const ligada = card.tags.some((t) => t.id === tag.id);
                  return (
                    <span key={tag.id} className="db-etq-envolt">
                      <button
                        type="button"
                        className={`db-etq db-etq-bt${ligada ? ' on' : ''}`}
                        style={{ '--c': tag.cor } as React.CSSProperties}
                        onClick={() => alternarTag(tag.id)}
                      >
                        {tag.nome}
                      </button>
                      <button
                        type="button"
                        className="db-etq-cor"
                        style={{ '--c': tag.cor } as React.CSSProperties}
                        onClick={() => setPaletaDaTag(paletaDaTag === tag.id ? null : tag.id)}
                        title={`Mudar a cor de "${tag.nome}"`}
                        aria-label={`Mudar a cor de ${tag.nome}`}
                      />
                      <button
                        type="button"
                        className="db-etq-del"
                        onClick={() => apagarTag(tag)}
                        title={`Apagar a tag "${tag.nome}"`}
                        aria-label={`Apagar a tag ${tag.nome}`}
                      >
                        ×
                      </button>
                      {paletaDaTag === tag.id && (
                        <span className="db-etq-paleta">
                          {CORES.map((cor) => (
                            <button
                              key={cor}
                              type="button"
                              className={`db-etq-cor-op${cor === tag.cor ? ' on' : ''}`}
                              style={{ background: cor }}
                              onClick={() => editarTag.mutate({ id: tag.id, cor })}
                              aria-label={`Cor ${cor}`}
                            />
                          ))}
                        </span>
                      )}
                    </span>
                  );
                })}
                {criandoTag ? (
                  <input
                    className="db-etq-in"
                    value={nomeTag}
                    autoFocus
                    placeholder="Nome da tag"
                    onChange={(e) => setNomeTag(e.target.value)}
                    onBlur={() => {
                      if (nomeTag.trim()) criarTag.mutate(nomeTag.trim());
                      else setCriandoTag(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && nomeTag.trim()) criarTag.mutate(nomeTag.trim());
                      if (e.key === 'Escape') {
                        setCriandoTag(false);
                        setNomeTag('');
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="db-etq-nova"
                    onClick={() => setCriandoTag(true)}
                  >
                    + tag
                  </button>
                )}
              </div>
            </>
          }
        />
      </div>
    </div>
  );
}

